/**
 * Pipeline Configuration API
 */
import apiClient from './index'
import type { ApiResponse } from '@/types'

export interface PipelineStage {
  id: string
  name: string
  probability: number
  color: string
  order: number
  is_closed: boolean
  is_won: boolean
}

export interface PipelineConfig {
  id: string
  name: string
  stages: PipelineStage[]
  is_active: boolean
}

export interface PipelineSummaryData {
  by_stage: Record<string, {
    name: string
    color: string
    order: number
    count: number
    total_value: number
    weighted_value: number
  }>
  stages: PipelineStage[]
  totals: {
    count: number
    total_value: number
    weighted_value: number
  }
}

export const pipelineApi = {
  /**
   * Get pipeline configuration
   */
  async getConfig(): Promise<PipelineConfig> {
    const response = await apiClient.get<ApiResponse<PipelineConfig>>('/pipeline/config')
    return response.data.data
  },

  /**
   * Update pipeline configuration
   */
  async updateConfig(data: Partial<PipelineConfig>): Promise<PipelineConfig> {
    const response = await apiClient.put<ApiResponse<PipelineConfig>>('/pipeline/config', data)
    return response.data.data
  },

  /**
   * Get pipeline stages
   */
  async getStages(): Promise<PipelineStage[]> {
    const response = await apiClient.get<ApiResponse<PipelineStage[]>>('/pipeline/stages')
    return response.data.data
  },

  /**
   * Add a new stage
   */
  async addStage(stage: Partial<PipelineStage>): Promise<PipelineStage> {
    const response = await apiClient.post<ApiResponse<PipelineStage>>('/pipeline/stages', stage)
    return response.data.data
  },

  /**
   * Update a stage
   */
  async updateStage(stageId: string, data: Partial<PipelineStage>): Promise<PipelineStage> {
    const response = await apiClient.put<ApiResponse<PipelineStage>>(`/pipeline/stages/${stageId}`, data)
    return response.data.data
  },

  /**
   * Delete a stage
   */
  async deleteStage(stageId: string): Promise<void> {
    await apiClient.delete(`/pipeline/stages/${stageId}`)
  },

  /**
   * Reorder stages
   */
  async reorderStages(stageIds: string[]): Promise<PipelineStage[]> {
    const response = await apiClient.put<ApiResponse<PipelineStage[]>>('/pipeline/stages/reorder', {
      stage_ids: stageIds,
    })
    return response.data.data
  },

  /**
   * Reset to default configuration
   */
  async resetToDefault(): Promise<PipelineConfig> {
    const response = await apiClient.post<ApiResponse<PipelineConfig>>('/pipeline/reset')
    return response.data.data
  },

  /**
   * Get pipeline summary with deals
   */
  async getPipelineSummary(): Promise<PipelineSummaryData> {
    const response = await apiClient.get<ApiResponse<PipelineSummaryData>>('/deals/pipeline')
    return response.data.data
  },
}
